/**
 * Optimizely variable keys for `window.matchScreenRedesign.variables`.
 * Values MUST match Optimizely exactly — typos silently fall back to defaults.
 */

export const MODULE_KEYS = {
  heading: 'heading_module',
  trustSignals: 'trust_signals_module',
  conciergeBanner: 'concierge_banner_module',
  // Orchestrator: gates the tabbed UI and owns tab presence + order.
  matchTabs: 'match_tabs_module',
  advisorCards: 'advisor_cards_module',
  faq: 'faq_module',
  disclosure: 'disclosure_module',
  favCalculator: 'fav_calculator_module',
  ads: 'ads_module',
  csat: 'csat_module',
  articleCarousel: 'article_carousel_module',
  selfScheduling: 'self_scheduling_module',
  feedback: 'feedback_module'
} as const

export const CONCIERGE_BANNER_DEFAULTS = {
  enabled: true,
  image: {
    url: 'https://captivate.smartasset.com/neil-ms-redesign.png',
    altText: "Neil's photo"
  },
  heading: "Hi, I'm Neil, Manager of the {highlight}Concierge{/highlight} Team.",
  subheading: "We're here to make finding the right financial partner easy. Here is your game plan",
  listOfSteps: [
    "{bold}Expect a call asap.{/bold} A representative from our concierge team will be reaching out shortly. We'll discuss your goals, review your matches together, and connect you directly with an advisor.",
    '{bold}Review your matches.{/bold} Read through the advisor profiles below at your own pace to compare their services and specialties.',
    "{bold}Connect for free.{/bold} No cost, no commitment, it's just a conversation."
  ],
  mobile: {
    subheading: "We're here to make finding the right financial partner easy. Here is your game plan:",
    toggleText: 'See how it works'
  }
}

export const HEADING_DEFAULTS = {
  enabled: true,
  heading: 'Great News! We Found Your Fiduciary Matches.',
  subheading: 'Your personalized advisor matches are ready.'
}

export const TRUST_SIGNALS_DEFAULTS = {
  enabled: true,
  bbb: true,
  trustpilot: true,
  smartasset: true
} as const

/**
 * Orchestrator defaults for `match_tabs_module`. `tabs` is an ordered list of
 * tab descriptors: `module` references a content variable (must be present in
 * MatchTabsModule's TAB_REGISTRY — unknown/missing modules are dropped at
 * runtime), and `title` is the tab label (flag-controllable, so experiments can
 * relabel tabs without a deploy; falls back to the registry default if blank).
 * Array order is render order.
 */
export type MatchTabsConfig = {
  enabled: boolean
  tabs: Array<{ title?: string, module: string }>
}

export const MATCH_TABS_DEFAULTS: MatchTabsConfig = {
  enabled: true,
  tabs: [
    { title: 'Compare Your Matches', module: 'advisor_cards_module' },
    { title: 'FAQs', module: 'faq_module' }
  ]
}

/** Content for the FAQ tab (`faq_module`). */
export const FAQ_DEFAULTS = {
  faqHeading: 'Frequently Asked {highlight}Questions{/highlight}',
  faqs: [
    {
      question: 'What does SmartAsset do?',
      answer: 'SmartAsset is a personal finance company focused on empowering people to make smart financial decisions. We built this tool to help people find a financial advisor who can help you work toward achieving your financial goals. We help thousands of users a month in their search for a financial advisor.'
    },
    {
      question: 'How does this service work?',
      answer: 'We match you with up to three advisors. Before allowing an advisor on our platform, we verify their status as being registered or chartered as fiduciaries ("Adviser(s)") with a regulatory body in the United States.'
    },
    {
      question: 'How much does it cost?',
      answer: 'SmartAsset’s matching service doesn’t cost you anything. You only pay the advisor if you decide to work with them. The advisors on our platform have several different fee structures, so be sure to ask your matches about what they charge.'
    },
    {
      question: 'How should I prepare for my advisor matches?',
      answer: 'You can prepare for your conversation by reading our SmartReads article: [10 questions to ask an advisor](https://smartasset.com/financial-advisor/questions-to-ask-a-financial-advisor)'
    }
  ]
}

export const ADVISOR_CARDS_DEFAULTS = {
  enabled: true,
  typeLabels: {
    human: 'Vetted Advisor',
    firm: 'Vetted Firm',
    network: 'Vetted Firm',
    roboadvisor: 'Robo Advisor',
    experience: 'Experience',
    founded_in: 'Founded In',
    fee_structure: 'Fee Structure'
  },
  ctaLabels: {
    additionalInformation: 'Additional Information',
    roboAdvisorCTA: 'Get Started'
  },
  feeStructureTooltips: {
    hourly: 'You pay a set hourly rate for the time the advisor spends working with you, regardless of account size.',
    flatFee: 'You pay a fixed fee for a specific service or an ongoing retainer arrangement.',
    varies: 'This advisor may use different fee structures depending on the services you choose. Be sure to ask them directly for details on how they charge for their services.',
    commissionBased: 'The advisor earns commissions from certain financial products, mutual funds, or insurance products they recommend.',
    other: 'The advisor uses an alternative compensation structure. Be sure to ask them directly for details on how they charge for their services.',
    assetBased: 'You pay a percentage of the assets the advisor manages on your behalf (commonly called Assets Under Management, or AUM).',
    feeAndCommission: 'The advisor charges a direct fee for their advice, but may also earn commissions by selling certain financial products.',
    feeOnly: 'The advisor is compensated solely by direct fees paid by you. They do not earn commissions from selling financial products.',
    feeBased: 'The advisor charges a direct fee for their services, but may also earn commissions from selling specific financial products.'
  }
}

/**
 * Defaults for `disclosure_module`. Only `heading` and `subheading` are
 * flag-controllable; per-advisor name, body, link URL, and link text come from
 * the matches context endpoint (see DisclosureBlock).
 */
export const DISCLOSURE_DEFAULTS = {
  enabled: true,
  heading: 'Our Matching Service Is {highlight}100%{/highlight} Free To You',
  subheading: "You're not obligated to hire any advisors. SmartAsset is compensated by advisors to keep the service free."
} as const

/**
 * Defaults for `fav_calculator_module`. `heading`/`subheading` are flag-controllable and
 * forwarded to the CDS-served calculator iframe as `data-header-text` / `data-subheader-text`
 * (see FavCalculatorModule). Used as the fallback when the Optimizely variable is present but
 * a field is missing/unparseable.
 */
export const FAV_CALCULATOR_DEFAULTS = {
  enabled: true,
  heading: 'See what an advisor could mean for your future',
  subheading: 'Estimate the long-term lift a financial advisor could add to your net worth.'
} as const

export const DISCLOSURE_DEFAULT_BODY = 'Advisor compensates SmartAsset for new leads. SmartAsset is not an investment client of Advisor. SmartAsset has an economic incentive to recommend Advisor, thereby resulting in a material conflict of interest.'

export const DISCLOSURE_DEFAULT_LINK_TEXT = 'Details regarding fee structure can be found here.'

export const ADS_DEFAULTS = {
  enabled: true
}

/**
 * Defaults for CSAT module mirror the legacy ExpandedFeedbackModule wording. Flag-controllable so experiments can hide this module
 * and tweak phrasing without a deploy. The star scale 1-5 is hardcoded.
 */
export const CSAT_DEFAULTS = {
  enabled: true,
  heading: 'Share Your Feedback',
  question: 'How would you rate your experience using SmartAsset’s financial advisor matching service so far?',
  lowLabel: 'Needs Improvement',
  highLabel: 'Excellent',
  commentPrompt: 'What is the most important reason for that score? (optional)',
  submitLabel: 'Submit',
  thanksMessage: 'Thank you for your answer! Your feedback is very important to us.'
} as const

/**
 * Defaults for the feedback module which succeeds the CSAT module allowing for defaults for 3rd party links
 */
export const FEEDBACK_DEFAULTS = {
  enabled: false,
  heading: "We'd like to hear from you!",
  subheading: 'Your feedback makes a difference. Please leave a review on WalletHub or contact us directly.',
  externalCTA: 'Review Us On WalletHub',
  externalCTALink: 'https://wallethub.com/profile/smartasset-13760810i',
  internalCTA: 'Contact Us',
  completedCTA: 'Submit',
  internalHeading: 'How was your experience?',
  internalSubheading: 'What is the most important reason for that score? (optional)',
  completedHeading: 'Thank you for your answer!',
  completedSubheading: 'Your feedback is very important to us.'
} as const

/**
 * Defaults for `article_carousel_module`. `heading` and `subheading` are
 * flag-controllable; the heading supports the `{highlight}` token to render a
 * word in brand blue.
 */
export const ARTICLE_CAROUSEL_DEFAULTS = {
  enabled: true,
  heading: 'Get ready to meet your {highlight}matches{/highlight}',
  subheading: 'A SmartAsset concierge is reaching out to you shortly. In the meantime, explore these free guides to help prepare for your free introductory call.'
}

export const SELF_SCHEDULING_DEFAULTS = {
  enabled: false, // must be explicitly enabled in the variation
  scheduleButtonCardCTA: 'Schedule Call',
  scheduleButtonModalCTA: 'Schedule Call',
  confirmationHeader: "You're All Set!",
  confirmationSubtext: 'Your scheduling request has been received.',
  confirmationText: 'A member of the SmartAsset concierge team will be reaching out to you shortly to verify your information and confirm your appointment details.'
}

export type ModuleKey = typeof MODULE_KEYS[keyof typeof MODULE_KEYS]
